import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { updateProfile, deactivateAccount } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ChartBar,
  ArrowLeft,
  User as UserIcon,
  Crown,
  CheckCircle,
  FloppyDisk,
  WarningCircle,
  Trash,
} from "@phosphor-icons/react";
import { DATE_FORMATS, DEFAULT_DATE_FORMAT, setDateFormat, formatDate } from "@/lib/dates";
import HaveAQuestionBadge from "@/components/HaveAQuestionBadge";
import ThemeToggle from "@/components/ThemeToggle";
import PasswordInput from "@/components/PasswordInput";
import Brandmark from "@/components/Brandmark";

export default function AccountSettings() {
  const navigate = useNavigate();
  const { user, loading, refreshMe } = useAuth();
  const [displayName, setDisplayName] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [dateFmt, setDateFmt] = useState(DEFAULT_DATE_FORMAT);
  const [notifUpdates, setNotifUpdates] = useState(true);
  const [notifMarketing, setNotifMarketing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate("/login", { replace: true });
      return;
    }
    setDisplayName(user.display_name || user.name || "");
    setAvatarUrl(user.avatar_url || user.picture || "");
    setDateFmt(user.date_format || DEFAULT_DATE_FORMAT);
    const n = user.notifications || {};
    setNotifUpdates(n.updates !== false); // default true
    setNotifMarketing(!!n.marketing);
  }, [user, loading, navigate]);

  const nameValid = () => {
    const v = displayName.trim();
    if (!v) return true; // empty is fine — server keeps it empty
    return /^[A-Za-z0-9 ]{1,20}$/.test(v);
  };

  const save = async (e) => {
    e?.preventDefault?.();
    if (!nameValid()) {
      toast.error("Display name must be alphanumeric and ≤ 20 chars");
      return;
    }
    setSaving(true);
    try {
      await updateProfile({
        display_name: displayName.trim(),
        avatar_url: avatarUrl.trim(),
        date_format: dateFmt,
        notifications: { updates: notifUpdates, marketing: notifMarketing },
      });
      setDateFormat(dateFmt); // cascade to every date field in the app
      toast.success("Settings saved");
      await refreshMe?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !user) {
    return <div className="min-h-screen w-full bg-slate-50" />;
  }

  const tier = user.tier || "free";
  const email = user.email;
  const previewDate = formatDate(new Date().toISOString().slice(0, 10), dateFmt);

  return (
    <div className="min-h-screen w-full bg-slate-50" data-testid="account-settings-page">
      {/* Header */}
      <div className="h-16 px-8 flex items-center justify-between border-b border-slate-200 bg-white sticky top-0 z-10">
        <Link to="/dashboard" className="flex items-center gap-3" data-testid="settings-back-logo">
          <Brandmark size={36} />
          <div>
            <div className="font-display text-lg font-black tracking-tight leading-none">
              ThatGantt
            </div>
            <div className="text-[10px] font-mono-ibm text-slate-500 uppercase tracking-widest mt-1">
              Account Settings
            </div>
          </div>
        </Link>
        <div className="flex items-center gap-3">
          <ThemeToggle />
          <Button
            variant="outline"
            onClick={() => navigate("/dashboard")}
            className="h-9 rounded-md border-slate-300 text-slate-700 hover:text-slate-950 font-medium"
            data-testid="settings-back-btn"
          >
            <ArrowLeft size={14} weight="bold" />
            <span className="ml-2">Back to dashboard</span>
          </Button>
        </div>
      </div>

      <form onSubmit={save} className="max-w-3xl mx-auto px-8 py-10 space-y-8">
        <div>
          <h1 className="font-display text-4xl font-black tracking-tight text-slate-950">
            Settings
          </h1>
          <p className="text-sm text-slate-500 mt-1.5">
            Your profile, date format, and notification preferences — synced everywhere.
          </p>
        </div>

        {/* Profile card */}
        <Card title="Profile" testid="settings-profile-card">
          <div className="flex items-start gap-6">
            <AvatarPreview src={avatarUrl} name={displayName || email} />
            <div className="flex-1 space-y-4">
              <Field label="Display name" hint="Shown on project cards and the Gantt header. Alphanumeric, max 20 chars.">
                <Input
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="e.g. Justin C"
                  maxLength={20}
                  className={`h-10 rounded-md border-slate-300 focus-visible:ring-0 ${
                    nameValid() ? "focus-visible:border-slate-950" : "border-red-500 focus-visible:border-red-500"
                  }`}
                  data-testid="settings-display-name"
                />
                {!nameValid() && (
                  <div className="text-[11px] text-red-600 mt-1">Letters, numbers and spaces only — up to 20 characters.</div>
                )}
              </Field>
              <Field label="Avatar URL" hint="Paste a link to an image (we'll add upload in a future release).">
                <Input
                  value={avatarUrl}
                  onChange={(e) => setAvatarUrl(e.target.value)}
                  placeholder="https://…"
                  className="h-10 rounded-md border-slate-300 focus-visible:ring-0 focus-visible:border-slate-950 font-mono-ibm text-xs"
                  data-testid="settings-avatar-url"
                />
              </Field>
              <div className="text-[11px] text-slate-500 font-mono-ibm">
                Signed in as <span className="text-slate-800">{email}</span>
              </div>
            </div>
          </div>
        </Card>

        {/* Plan card */}
        <Card title="Plan" testid="settings-plan-card">
          <div className="flex items-center justify-between gap-6">
            <div>
              <div className="text-[10px] uppercase tracking-widest font-mono-ibm text-slate-500">
                Current plan
              </div>
              <div className="flex items-center gap-2 mt-1">
                <div className="font-display text-2xl font-black tracking-tight text-slate-950 capitalize">
                  {tier}
                </div>
                {tier === "owner" && (
                  <Crown size={16} weight="fill" className="text-amber-500" />
                )}
              </div>
              <div className="text-xs text-slate-500 mt-1">
                {tier === "free" && "Up to 10 projects. Upgrade to remove the limit."}
                {tier === "starter" && "Up to 25 projects. Priority support."}
                {tier === "pro" && "Unlimited projects. Version history + advanced exports."}
                {tier === "team" && "Everything + Team SSO + custom workspaces."}
                {tier === "owner" && "Internal workspace — unlimited projects."}
              </div>
            </div>
            <Link to="/pricing">
              <Button variant="outline" className="h-10 rounded-md border-slate-300 hover:border-slate-950 text-slate-800 font-medium" data-testid="settings-change-plan">
                Change plan
              </Button>
            </Link>
          </div>
        </Card>

        {/* Date format card */}
        <Card title="Date format" testid="settings-date-card">
          <Field
            label="Preferred format"
            hint="Updates every date shown in projects, tasks, tooltips and exports."
          >
            <div className="flex items-center gap-3">
              <Select value={dateFmt} onValueChange={setDateFmt}>
                <SelectTrigger
                  className="h-10 w-[220px] rounded-md border-slate-300 text-sm focus:ring-0 focus:border-slate-950"
                  data-testid="settings-date-format"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-md">
                  {DATE_FORMATS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="text-[11px] font-mono-ibm text-slate-500">
                Preview: <span className="text-slate-950 font-semibold">{previewDate}</span>
              </div>
            </div>
          </Field>
          <div className="mt-3 text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-md p-3 leading-relaxed">
            Tip: when typing into a date field you can paste any common format
            (<code className="font-mono-ibm text-slate-700">01/24/2026</code>,{" "}
            <code className="font-mono-ibm text-slate-700">24-01-2026</code>,{" "}
            <code className="font-mono-ibm text-slate-700">Jan 24, 2026</code>) — we'll normalise it for you.
          </div>
        </Card>

        {/* Notifications card */}
        <Card title="Notifications" testid="settings-notifications-card">
          <div className="space-y-4">
            <ToggleRow
              testid="settings-notif-updates"
              label="Project updates"
              sub="Email me when someone changes a task, assigns me, or flags a risk on a project I'm on."
              checked={notifUpdates}
              onChange={setNotifUpdates}
            />
            <ToggleRow
              testid="settings-notif-marketing"
              label="Product news & tips"
              sub="Occasional updates about new features, templates and office hours. No spam, promise."
              checked={notifMarketing}
              onChange={setNotifMarketing}
            />
          </div>
        </Card>

        {/* Danger zone — account deactivation */}
        <DeactivateAccountCard userEmail={email} hasPassword={!!user?.auth_providers?.includes?.("password")} />

        <div className="flex items-center gap-3 justify-end sticky bottom-4">
          <Button
            type="submit"
            disabled={saving}
            className="h-11 rounded-md bg-slate-950 hover:bg-slate-800 text-white font-medium px-5 shadow-lg"
            data-testid="settings-save-btn"
          >
            <FloppyDisk size={14} weight="bold" />
            <span className="ml-2">{saving ? "Saving…" : "Save settings"}</span>
          </Button>
        </div>
      </form>

      <HaveAQuestionBadge />
    </div>
  );
}

function Card({ title, children, testid }) {
  return (
    <section
      className="bg-white border border-slate-300 rounded-md p-6"
      data-testid={testid}
    >
      <div className="text-[10px] uppercase tracking-widest font-mono-ibm text-slate-500 mb-4">
        {title}
      </div>
      {children}
    </section>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <Label className="text-xs font-semibold text-slate-950">{label}</Label>
      {hint && <div className="text-[11px] text-slate-500 mt-0.5 mb-1.5">{hint}</div>}
      {children}
    </div>
  );
}

function ToggleRow({ label, sub, checked, onChange, testid }) {
  return (
    <div className="flex items-start justify-between gap-6 py-1">
      <div className="flex-1">
        <div className="text-sm font-semibold text-slate-950">{label}</div>
        <div className="text-xs text-slate-500 mt-0.5 leading-relaxed">{sub}</div>
      </div>
      <Switch
        checked={checked}
        onCheckedChange={onChange}
        data-testid={testid}
      />
    </div>
  );
}

function AvatarPreview({ src, name }) {
  const initials = (name || "?").split(" ").slice(0, 2).map((p) => p[0]).join("").toUpperCase();
  return (
    <div className="h-20 w-20 rounded-full bg-ink text-neon flex items-center justify-center shrink-0 overflow-hidden shadow-md">
      {src ? (
        <img src={src} alt="" className="w-full h-full object-cover" />
      ) : (
        <span className="text-2xl font-black">{initials}</span>
      )}
    </div>
  );
}

function DeactivateAccountCard({ userEmail, hasPassword }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setConfirmEmail("");
    setPassword("");
  };

  const canConfirm =
    confirmEmail.trim().toLowerCase() === (userEmail || "").toLowerCase() &&
    (!hasPassword || password.length > 0);

  const submit = async () => {
    if (!canConfirm) return;
    setSubmitting(true);
    try {
      const res = await deactivateAccount(hasPassword ? password : null);
      const n = res?.deleted_projects ?? 0;
      toast.success(
        n > 0
          ? `Account deactivated. ${n} project${n === 1 ? "" : "s"} deleted.`
          : "Account deactivated.",
      );
      // Hard reload to reset any cached auth state
      window.location.replace("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't deactivate account");
      setSubmitting(false);
    }
  };

  return (
    <section
      className="bg-white border border-rose-300/60 dark:border-rose-500/30 rounded-md p-6"
      data-testid="settings-danger-zone-card"
    >
      <div className="text-[10px] uppercase tracking-widest font-mono-ibm text-rose-600 dark:text-rose-400 mb-4 flex items-center gap-2">
        <WarningCircle size={12} weight="fill" />
        Danger zone
      </div>
      <div className="flex items-start justify-between gap-6">
        <div className="flex-1">
          <div className="text-sm font-semibold text-slate-950">Deactivate account</div>
          <p className="text-xs text-slate-500 mt-1 leading-relaxed">
            Permanently deletes your account, all projects you own, all their tasks and version
            history, and removes you from any projects shared with you. This action{" "}
            <strong className="text-rose-600 dark:text-rose-400">cannot be undone.</strong>
          </p>
        </div>
        <Button
          type="button"
          onClick={() => {
            reset();
            setOpen(true);
          }}
          variant="outline"
          className="h-9 rounded-md border-rose-300 text-rose-700 hover:bg-rose-50 hover:border-rose-400 dark:border-rose-500/40 dark:text-rose-400 dark:hover:bg-rose-500/10 shrink-0"
          data-testid="open-deactivate-dialog-btn"
        >
          <Trash size={12} weight="bold" />
          <span className="ml-1.5">Deactivate…</span>
        </Button>
      </div>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
        <DialogContent className="rounded-md max-w-md" data-testid="deactivate-account-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black tracking-tight flex items-center gap-2">
              <WarningCircle size={20} weight="fill" className="text-rose-500" />
              Deactivate account
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              This will <strong>permanently</strong> delete your account and all projects you own.
              Anyone you've shared with will lose access. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-ibm">
                Type your email to confirm
              </Label>
              <Input
                value={confirmEmail}
                onChange={(e) => setConfirmEmail(e.target.value)}
                placeholder={userEmail}
                className="mt-1.5 h-10 rounded-md border-slate-300 focus-visible:ring-0 focus-visible:border-rose-500 font-mono-ibm text-xs"
                data-testid="deactivate-email-confirm"
                autoFocus
              />
            </div>
            {hasPassword && (
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-ibm">
                  Current password
                </Label>
                <PasswordInput
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Your password"
                  className="mt-1.5 h-10 rounded-md border-slate-300 focus-visible:ring-0 focus-visible:border-rose-500"
                  data-testid="deactivate-password"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              className="rounded-md border-slate-300"
              data-testid="deactivate-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={submit}
              disabled={!canConfirm || submitting}
              className="rounded-md bg-rose-600 hover:bg-rose-500 text-white"
              data-testid="deactivate-confirm-btn"
            >
              {submitting ? "Deactivating…" : "Deactivate account"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
